import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { getDeclineSuccessRoute } from '~/components/ConfirmCandidate/utils';
import { IJob, IMultipleCounters, IUser } from '~/models';

/** Shape `ConfirmOrDecline` hands back through `onConfirmOutcome`. */
interface IConfirmOutcome {
  outcome: { type: string; hasPendingJobs?: boolean };
  response?: { local_date?: string; profession?: string } | null;
}

interface IConfirmVariables {
  candidate?: IUser;
  officeId?: string;
  jobId?: string;
  counterOfferId?: string;
}

/** The rebook prompt shown when a confirmed professional has other shifts open. */
export interface IAnotherJobPrompt {
  candidate?: IUser;
  officeId?: string;
  hasPendingJobs: boolean;
  shiftId?: string;
  offerId?: string;
}

/** The eligible-jobs prompt shown when a confirm surfaces more shifts to fill. */
export interface IEligibleJobsPrompt {
  candidate?: IUser;
  officeId?: string;
  /** The confirm response, which the popup reads only for the shift's date + profession. */
  job?: { local_date?: string; profession?: string } | null;
}

interface IUseConfirmCandidateActionsArgs {
  job: Partial<IJob> | null | undefined;
  /** The candidate currently being decided on. */
  counter: IMultipleCounters | null | undefined;
  /** Lifts the navigation guard for an intentional navigation. */
  allowNavigation: () => void;
}

/**
 * The confirm/decline follow-ups the Confirm Candidates pages own.
 *
 * `ConfirmOrDecline` — reused wholesale from the legacy screen — owns the
 * buttons, the mutations, the decline and counter-shift popups, and every
 * `confirm_candidate` action event. What it deliberately does not own is what
 * happens *after* a successful confirm: the outcome can ask for a rebook prompt,
 * an eligible-jobs prompt, or nothing at all, and only the page knows where the
 * office should end up. This hook is that half.
 *
 * Reusing the legacy component rather than reimplementing it is what makes
 * "confirming works end to end" true by construction: a second copy of the
 * confirm mutation would be a second place for the PostHog events, the no-card
 * handling and the counter-shift confirmation to drift.
 *
 * State only — `ConfirmOutcomePopups` renders it. Keeping the JSX out means this
 * can be tested as the state machine it is.
 */
export const useConfirmCandidateActions = ({
  job,
  counter,
  allowNavigation,
}: IUseConfirmCandidateActionsArgs) => {
  const navigate = useNavigate();

  const [isConfirming, setIsConfirming] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [isDeclineSuccess, setIsDeclineSuccess] = useState(false);
  const [isPaymentMissing, setIsPaymentMissing] = useState(false);
  const [anotherJobPrompt, setAnotherJobPrompt] = useState<IAnotherJobPrompt | null>(null);
  const [eligibleJobsPrompt, setEligibleJobsPrompt] = useState<IEligibleJobsPrompt | null>(null);

  // Frozen at decline time: the decline invalidates the counter list, so by the
  // time the success popup closes the list has already shrunk and can no longer
  // answer whether other candidates were in play.
  const hadOtherCandidatesRef = useRef(false);

  // The declined professional's name, kept for the success popup — the counter
  // itself is gone from the refetched list by then.
  const declinedNameRef = useRef('');

  const handleDeclineConfirmed = useCallback(
    (hasMultiple: boolean) => {
      hadOtherCandidatesRef.current = hasMultiple;
      declinedNameRef.current = [counter?.candidate?.first_name, counter?.candidate?.last_name]
        .filter(Boolean)
        .join(' ');
    },
    [counter?.candidate?.first_name, counter?.candidate?.last_name],
  );

  const handleConfirmOutcome = useCallback(
    ({ outcome, response }: IConfirmOutcome, variables: IConfirmVariables) => {
      if (outcome.type === 'show_another_job_popup') {
        setAnotherJobPrompt({
          candidate: variables.candidate,
          officeId: variables.officeId,
          hasPendingJobs: Boolean(outcome.hasPendingJobs),
          // Taken from the confirm variables rather than from current state: the
          // confirm invalidates the job query, so the selected counter can have
          // moved on by the time the popup opens.
          shiftId: variables.jobId,
          offerId: variables.counterOfferId,
        });
        return;
      }

      if (outcome.type === 'show_eligible_jobs_popup') {
        setEligibleJobsPrompt({
          candidate: variables.candidate,
          officeId: variables.officeId,
          job: response ?? null,
        });
      }
    },
    [],
  );

  const closeDeclineSuccess = useCallback(() => {
    allowNavigation();
    navigate(
      getDeclineSuccessRoute({
        hasMultipleCounters: hadOtherCandidatesRef.current,
        jobId: job?.id,
        postingId: job?.postingId,
      }),
      { replace: true },
    );
    setIsDeclineSuccess(false);
  }, [allowNavigation, job?.id, job?.postingId, navigate]);

  return {
    /** Spread onto the reused legacy `ConfirmOrDecline`. */
    confirmOrDeclineProps: {
      candidateId: counter?.candidate?.id,
      currentCounter: counter,
      allowNavigation,
      onDeclineConfirmed: handleDeclineConfirmed,
      onConfirmOutcome: handleConfirmOutcome,
      onConfirmPendingChange: setIsConfirming,
      onDeclinePendingChange: setIsDeclining,
      onDeclineSuccess: () => setIsDeclineSuccess(true),
      onPaymentMissing: () => setIsPaymentMissing(true),
    },
    /** Everything `ConfirmOutcomePopups` needs to render, and nothing else. */
    outcome: {
      anotherJobPrompt,
      eligibleJobsPrompt,
      isPaymentMissing,
      isDeclineSuccess,
      declinedName: declinedNameRef.current,
      dismissAnotherJob: () => setAnotherJobPrompt(null),
      dismissEligibleJobs: () => setEligibleJobsPrompt(null),
      dismissPaymentMissing: () => setIsPaymentMissing(false),
      closeDeclineSuccess,
    },
    /** A confirm or decline is in flight — the page shows its loading state. */
    isMutating: isConfirming || isDeclining,
    /** A prompt is driving navigation; the page must not also render its content. */
    hasBlockingPrompt: anotherJobPrompt !== null || eligibleJobsPrompt !== null,
    /**
     * A popup is standing on the outcome of a confirm or a decline, so the page
     * must survive the status change that outcome caused.
     *
     * Wider than `hasBlockingPrompt` on purpose: that one answers "should the
     * page stop rendering its content", which the decline-success popup does not
     * need. This one answers "is the page still needed", and a decline of the
     * last candidate reopens the shift — which would swap the page out from under
     * that popup exactly as a confirm does.
     */
    hasOpenEpilogue:
      anotherJobPrompt !== null ||
      eligibleJobsPrompt !== null ||
      isDeclineSuccess ||
      isPaymentMissing,
  };
};
